// src/shared/contexts/AuthContext.tsx
import React, { createContext, useContext, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { performLogoutCleanup } from '../utils/logoutCleanup';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: unknown | null;
  login: () => void;
  signup: () => void;
  logout: () => void;
  userRole: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: auth0Logout
  } = useAuth0();

  // Login function
  const login = () => loginWithRedirect();
  
  // Signup function (redirects to signup tab)
  const signup = () => loginWithRedirect({
    authorizationParams: {
      screen_hint: 'signup'
    }
  });
  
  // Logout function
  const logout = () => {
    // Perform complete cleanup of all cache and storage
    performLogoutCleanup();

    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
        federated: true,
      }
    });
  };

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    user,
    login,
    signup,
    logout,
    userRole: null, // You'll implement this later
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};